import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import {
  AddCircleIcon,
  ChevronDownIcon,
} from '@tix-ticketing/ui-icons/outline';
import type { ReactNode } from 'react';
import { useIsMobile } from '../hooks/use-is-mobile';
import type { CurrencyData, ServiceData } from '../types';
import { FormattedPrice } from '../utils/formatted-price';
import { IconOnlyButton } from './button/icon-only-button';

export const ProductAccordion = ({
  services = [],
  defaultExpanded,
  currencyData,
  inAside = false,
  onAdd,
  title,
  addLabel,
  addLabelShort,
  emptyText,
  children,
}: {
  /** Order services to list; ignored when `children` supplies the rows. */
  services?: ServiceData[];
  defaultExpanded?: boolean;
  currencyData?: CurrencyData;
  inAside?: boolean;
  /** Shows the add button, which calls this. */
  onAdd?: () => void;
  title: ReactNode;
  /** Add button label. */
  addLabel?: ReactNode;
  /** Add button label on mobile and in the aside; falls back to `addLabel`. */
  addLabelShort?: ReactNode;
  /** Shown when there are neither rows nor `services`. */
  emptyText?: ReactNode;
  /** Rows rendered in place of the default `services` rows. */
  children?: ReactNode;
}) => {
  const isMobile = useIsMobile() || inAside;
  const emptyState = emptyText && (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {emptyText}
    </Typography>
  );

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        boxShadow: 'unset',
        background: 'transparent',
        '&:before': {
          display: 'none', // removes the default divider line
        },
      }}
    >
      <AccordionSummary
        sx={{
          minHeight: 0,
          p: 0,
          fontWeight: 500,
          flexDirection: 'row-reverse',
          '& .MuiAccordionSummary-content': {
            my: 0,
          },
        }}
        expandIcon={
          <IconOnlyButton
            component="span"
            variant="contained"
            color="monochrome"
            size="small"
            sx={{
              color: 'action.active',
              '&:hover': {
                color: 'action.active',
              },
            }}
          >
            <ChevronDownIcon />
          </IconOnlyButton>
        }
        aria-controls="product-accordion-content"
        id="product-accordion-header"
      >
        <Stack
          direction="row"
          sx={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="body1Bold"
            sx={{
              ml: 1.5,
            }}
          >
            {title}
          </Typography>
          {onAdd && (
            <Button
              component="div"
              role="button"
              color="monochrome"
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              endIcon={<AddCircleIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onAdd();
              }}
            >
              {isMobile ? (addLabelShort ?? addLabel) : addLabel}
            </Button>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          p: 0,
          pt: { xs: 1.5, sm: inAside ? 1.5 : 2 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {children ??
          (services.length > 0
            ? services.map((service) => (
                <Box
                  key={service.orderServiceId}
                  sx={{
                    p: 2,
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: (theme) =>
                      theme.palette.containerBackground.main,
                    borderRadius: (theme) => `${theme.formItemRadius.main}px`,
                  }}
                >
                  <Typography variant="body2Medium">{service.name}</Typography>
                  <Typography variant="body2Medium">
                    <FormattedPrice
                      amount={service.priceWithTax ?? 0}
                      currencyData={currencyData}
                    />
                  </Typography>
                </Box>
              ))
            : emptyState)}
      </AccordionDetails>
    </Accordion>
  );
};
